import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import DashboardLayout from '@/components/feature/DashboardLayout';
import { api, getApiErrorMessage, ApiError } from '@/services/api';
import { useToast } from '@/hooks/useToast';
import { useTheme } from '@/hooks/useTheme';

interface Counterparty {
  id: string;
  name: string;
  shortName: string;
  edrpou: string;
  country: string;
  status: string;
  onboardingStatus: string;
  assetsCount: number;
  createdAt: string;
}

export default function CounterpartiesPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { addToast } = useToast();
  const { isDark } = useTheme();
  const [items, setItems] = useState<Counterparty[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const cardBg = isDark ? '#1A1A1A' : '#fff';
  const cardBorder = isDark ? 'rgba(255,255,255,0.07)' : '#E5E5E8';
  const textMain = isDark ? '#E0E0E0' : '#1A1A1A';
  const textMuted = isDark ? '#6B6B70' : '#6B6B70';
  const inputBg = isDark ? 'rgba(255,255,255,0.05)' : '#F5F5F7';
  const inputBorder = isDark ? 'rgba(255,255,255,0.1)' : '#E5E5E8';
  const tableHeaderBg = isDark ? 'rgba(255,255,255,0.03)' : '#F5F5F7';
  const tableBorder = isDark ? 'rgba(255,255,255,0.05)' : '#F5F5F7';
  const separator = isDark ? 'rgba(255,255,255,0.07)' : '#E5E5E8';
  const hoverBg = isDark ? 'rgba(255,255,255,0.04)' : '#F9F9FB';
  const iconBg = isDark ? 'rgba(255,255,255,0.05)' : '#F5F5F7';
  const selectBg = isDark ? '#1A1A1A' : '#fff';

  useEffect(() => {
    loadCounterparties();
  }, []);

  const loadCounterparties = async () => {
    setLoading(true);
    try {
      const data = await api.get<Counterparty[]>('/counterparties');
      setItems(data);
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        setItems([]);
      } else {
        const message = getApiErrorMessage(err, 'Failed to load counterparties');
        addToast(message, 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  const filtered = items.filter((c) =>
    c.name.toLowerCase().includes(search.toLowerCase()) ||
    c.edrpou.includes(search)
  );

  const getStatusBadge = (status: string) => {
    const map: Record<string, { bg: string; color: string }> = {
      active: { bg: 'rgba(13,122,60,0.1)', color: '#0D7A3C' },
      pending: { bg: 'rgba(184,134,11,0.1)', color: '#B8860B' },
      suspended: { bg: 'rgba(209,34,41,0.1)', color: '#D12229' },
      draft: { bg: 'rgba(107,107,112,0.1)', color: '#6B6B70' },
    };
    const style = map[status] ?? map.draft;
    return (
      <span
        className="text-xs font-medium px-2.5 py-1 rounded-full capitalize"
        style={{ background: style.bg, color: style.color }}
      >
        {status}
      </span>
    );
  };

  const getOnboardingBadge = (status: string) => {
    const map: Record<string, { bg: string; color: string }> = {
      completed: { bg: 'rgba(13,122,60,0.1)', color: '#0D7A3C' },
      in_progress: { bg: 'rgba(31,91,143,0.1)', color: '#1F5B8F' },
      not_started: { bg: 'rgba(107,107,112,0.1)', color: '#6B6B70' },
    };
    const style = map[status] ?? map.not_started;
    return (
      <span
        className="text-xs font-medium px-2.5 py-1 rounded-full capitalize"
        style={{ background: style.bg, color: style.color }}
      >
        {status.replace('_', ' ')}
      </span>
    );
  };

  return (
    <DashboardLayout breadcrumb={[t('cp_title')]}>
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: textMain, fontFamily: 'Inter, sans-serif' }}>
            {t('cp_title')}
          </h1>
          <p className="text-sm mt-1" style={{ color: textMuted }}>
            {t('cp_subtitle')}
          </p>
        </div>
        <button
          onClick={() => navigate('/onboarding')}
          className="px-4 py-2.5 rounded-md text-sm font-medium text-white cursor-pointer transition-colors whitespace-nowrap"
          style={{ background: '#D12229' }}
        >
          <i className="ri-add-line mr-1"></i> {t('cp_new')}
        </button>
      </div>

      <div className="rounded-xl" style={{ background: cardBg, border: `1px solid ${cardBorder}` }}>
        <div className="flex items-center gap-3 p-4" style={{ borderBottom: `1px solid ${separator}` }}>
          <div className="flex items-center gap-2 px-3 py-2 rounded-md flex-1 max-w-md" style={{ background: inputBg, border: `1px solid ${inputBorder}` }}>
            <i className="ri-search-line text-sm" style={{ color: textMuted }}></i>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('cp_search')}
              className="bg-transparent outline-none text-sm flex-1"
              style={{ color: textMain }}
            />
          </div>
          <button
            onClick={loadCounterparties}
            className="w-9 h-9 flex items-center justify-center rounded-md cursor-pointer transition-colors"
            style={{ border: `1px solid ${inputBorder}`, background: selectBg }}
            title="Refresh"
          >
            <i className="ri-refresh-line text-sm" style={{ color: textMuted }}></i>
          </button>
        </div>

        {loading ? (
          <div className="p-12 text-center">
            <i className="ri-loader-4-line animate-spin text-2xl" style={{ color: textMuted }}></i>
            <p className="text-sm mt-3" style={{ color: textMuted }}>{t('cp_loading')}</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center">
            <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3" style={{ background: iconBg }}>
              <i className="ri-building-2-line text-xl" style={{ color: textMuted }}></i>
            </div>
            <p className="text-sm font-medium" style={{ color: textMain }}>{t('cp_not_found')}</p>
            <p className="text-xs mt-1" style={{ color: textMuted }}>
              {search ? t('cp_filter_hint') : t('cp_not_found_hint')}
            </p>
            {!search && (
              <button
                onClick={() => navigate('/onboarding')}
                className="mt-4 px-4 py-2 rounded-md text-sm font-medium text-white cursor-pointer whitespace-nowrap"
                style={{ background: '#D12229' }}
              >
                {t('cp_start_onboarding')}
              </button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr style={{ background: tableHeaderBg }}>
                  <th className="text-left px-4 py-3 font-semibold text-xs uppercase tracking-wider" style={{ color: textMuted }}>{t('cp_col_name')}</th>
                  <th className="text-left px-4 py-3 font-semibold text-xs uppercase tracking-wider" style={{ color: textMuted }}>{t('cp_col_edrpou')}</th>
                  <th className="text-left px-4 py-3 font-semibold text-xs uppercase tracking-wider" style={{ color: textMuted }}>{t('cp_col_country')}</th>
                  <th className="text-left px-4 py-3 font-semibold text-xs uppercase tracking-wider" style={{ color: textMuted }}>{t('cp_col_status')}</th>
                  <th className="text-left px-4 py-3 font-semibold text-xs uppercase tracking-wider" style={{ color: textMuted }}>{t('cp_col_onboarding')}</th>
                  <th className="text-left px-4 py-3 font-semibold text-xs uppercase tracking-wider" style={{ color: textMuted }}>{t('cp_col_assets')}</th>
                  <th className="text-left px-4 py-3 font-semibold text-xs uppercase tracking-wider" style={{ color: textMuted }}>{t('cp_col_created')}</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((c) => (
                  <tr
                    key={c.id}
                    className="cursor-pointer transition-colors"
                    onClick={() => navigate(`/counterparties/${c.id}`)}
                    style={{ borderBottom: `1px solid ${tableBorder}` }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = hoverBg; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                  >
                    <td className="px-4 py-3">
                      <div className="font-medium" style={{ color: textMain }}>{c.name}</div>
                      <div className="text-xs" style={{ color: textMuted }}>{c.shortName}</div>
                    </td>
                    <td className="px-4 py-3 font-mono text-xs" style={{ color: textMain }}>{c.edrpou}</td>
                    <td className="px-4 py-3" style={{ color: textMain }}>{c.country}</td>
                    <td className="px-4 py-3">{getStatusBadge(c.status)}</td>
                    <td className="px-4 py-3">{getOnboardingBadge(c.onboardingStatus)}</td>
                    <td className="px-4 py-3">
                      <span className="text-xs font-medium px-2 py-1 rounded-full" style={{ background: iconBg, color: textMain }}>
                        {c.assetsCount}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs" style={{ color: textMuted }}>
                      {new Date(c.createdAt).toLocaleDateString('uk-UA')}
                    </td>
                    <td className="px-4 py-3">
                      <button className="w-8 h-8 flex items-center justify-center rounded-md cursor-pointer transition-colors"
                        onMouseEnter={(e) => { e.currentTarget.style.background = iconBg; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                      >
                        <i className="ri-arrow-right-s-line" style={{ color: textMuted }}></i>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}